import { apiFetch } from './apiFetch';
import { ensureCsrfToken } from './publicApi';

const API_BASE = process.env.REACT_APP_API_URL;

// `acknowledgements` is keyed by consent purpose - { dpdp_notice, terms, age_declaration }
// - rather than being one `consentAccepted` boolean (Wave C item C3, finding F13). Three
// different kinds of thing were being collected on one tick: a privacy notice, a contract
// acceptance and a statutory declaration. Only the first is DPDP consent at all, and the
// server refuses each by name so the form can mark the box the user actually missed.
export async function signup(email, password, referredBy, acknowledgements, dob) {
  await ensureCsrfToken();
  const res = await apiFetch(`${API_BASE}/auth/signup`, {
    method: 'POST',
    body: JSON.stringify({ email, password, referredBy, acknowledgements, dob })
  });
  return res.json();
}

export async function login(email, password, verificationCode) {
  await ensureCsrfToken();
  const body = { email, password };
  if (verificationCode) body.verificationCode = verificationCode;
  const res = await apiFetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
  return res.json();
}

export async function logout() {
  try {
    await apiFetch(`${API_BASE}/auth/logout`, { method: 'POST' });
  } finally {
    sessionStorage.clear();
  }
}

export async function requestPasswordReset(email) {
  await ensureCsrfToken();
  const res = await apiFetch(`${API_BASE}/auth/request-password-reset`, {
    method: 'POST',
    body: JSON.stringify({ email })
  });
  return res.json();
}

export async function resetPassword(email, token, newPassword) {
  await ensureCsrfToken();
  const res = await apiFetch(`${API_BASE}/auth/reset-password`, {
    method: 'POST',
    body: JSON.stringify({ email, token, newPassword })
  });
  return res.json();
}

export async function changePassword(currentPassword, newPassword) {
  const res = await apiFetch(`${API_BASE}/auth/change-password`, {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword })
  });
  return res.json();
}

export async function getMe() {
  const res = await apiFetch(`${API_BASE}/auth/me`);
  return res.json();
}

export async function updateMe(updateData) {
  const res = await apiFetch(`${API_BASE}/auth/me`, {
    method: 'PUT',
    body: JSON.stringify(updateData)
  });
  return res.json();
}

export async function deleteMe(password) {
  const res = await apiFetch(`${API_BASE}/auth/me`, {
    method: 'DELETE',
    body: JSON.stringify({ password })
  });
  return res.json();
}

export const getReferralInfo = async () => {
  const response = await apiFetch(`${API_BASE}/auth/referral-info`);
  return response.json();
};

export const requestReferralClaim = async (referralUserId, upiId) => {
  const response = await apiFetch(`${API_BASE}/auth/referral-claim`, {
    method: 'POST',
    body: JSON.stringify({ referralUserId, upiId })
  });
  return response.json();
};

export async function requestDataUnlock() {
  const res = await apiFetch(`${API_BASE}/auth/request-unlock-data`, { method: 'POST' });
  return res.json();
}

export async function unlockData(dataToken) {
  const res = await apiFetch(`${API_BASE}/auth/unlock-data`, {
    method: 'POST',
    body: JSON.stringify({ dataToken })
  });
  return res.json();
}

export async function lockData() {
  const res = await apiFetch(`${API_BASE}/auth/lock-data`, { method: 'POST' });
  return res.json();
}

export async function updateConsent(acknowledgements) {
  const res = await apiFetch(`${API_BASE}/auth/update-consent`, {
    method: 'POST',
    body: JSON.stringify({ acknowledgements })
  });
  return res.json();
}

export async function exportPersonalData(password, format = 'json') {
  const normalizedFormat = String(format || 'json').toLowerCase();
  const res = await apiFetch(`${API_BASE}/auth/export`, {
    method: 'POST',
    // personalDataExportLimiter (server/utils/rateLimiter.js) runs before
    // authenticateToken, so it has no req.user to scope by -- it falls back
    // to an IP-only key unless the request body carries an email, same as
    // every other unauthenticated-at-that-point rate-limited route (login,
    // password reset). Sending the logged-in user's own email here scopes
    // export rate limiting per-account instead of sharing one bucket across
    // every export request from the same IP.
    body: JSON.stringify({ password, format: normalizedFormat, email: sessionStorage.getItem('email') || undefined })
  });

  if (normalizedFormat === 'csv') {
    if (!res.ok) {
      return res.json();
    }
    const contentDisposition = res.headers.get('content-disposition') || '';
    const fileNameMatch = contentDisposition.match(/filename="([^"]+)"/i);
    const csv = await res.text();
    return {
      success: true,
      format: 'csv',
      csv,
      filename: fileNameMatch ? fileNameMatch[1] : `personal-data-export-${Date.now()}.csv`
    };
  }

  return res.json();
}
